"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DealTagRegistrySchema = exports.DealTagSchema = exports.DealSchema = exports.DealPricingHistoryEntrySchema = void 0;
exports.dealPackagesTotals = dealPackagesTotals;
exports.appendPricingHistory = appendPricingHistory;
exports.pricingChanged = pricingChanged;
exports.dealStrikethrough = dealStrikethrough;
const zod_1 = require("zod");
const planAssignment_1 = require("./planAssignment");
const dealDevice_1 = require("./dealDevice");
const pricePhases_1 = require("./pricePhases");
const dealOffer_1 = require("./dealOffer");
const dealDocuments_1 = require("./dealDocuments");
/**
 * DEAL — a priced, reusable signup offer a super admin builds once (spec
 * §12/A2, A3). Where a `PlanAssignment` (see `planAssignment.ts`) records
 * what ONE tenant was sold, a Deal is the TEMPLATE many signups draw from —
 * the user's own words: *"we discount amount or percentage discount to get
 * to the price we want, with devices and software packages. so we know
 * exactly, for this tenant, how each of these discounts apply to which
 * packages."* Tasks 8/10b/12 convert a redeemed Deal into a PlanAssignment,
 * which is why `packageDiscounts` below reuses the EXACT SAME
 * `DiscountSchema` a PlanAssignment's `discounts.perPackage` uses — one
 * discount shape, not two that could drift apart in translation.
 *
 * `super-admin-ext`'s `deals.ts` predates this package and stores a Deal as
 * a hand-built DynamoDB item — this schema does not change that storage.
 * It types the SUBSET of a Deal worth centralising (its pricing fields), so
 * every consumer (this task's handlers, and Tasks 8/10b/12 downstream)
 * shares one definition instead of hand-copying the shape three times.
 *
 * Every field is OPTIONAL: a deal created before this schema existed
 * carries none of the new ones, and the platform's shared-types rule is
 * that an added field is never required — a legacy deal must still be a
 * valid `Deal`. `.passthrough()` because this schema types a subset, not
 * the whole row: fields deals.ts already stores (commissionMode, flatFee,
 * usedCount, ...) are not modelled here and must survive being parsed
 * through this schema unchanged rather than being stripped.
 */
// --- Provenance (2026-08-24 spec §3.4) — ADMIN-ONLY, additive ---
// Declared ABOVE DealSchema: DealSchema's own `pricingHistory` field embeds
// this schema, so it must exist before DealSchema is constructed.
exports.DealPricingHistoryEntrySchema = zod_1.z.object({
    at: zod_1.z.number(),
    by: zod_1.z.string(),
    priceCents: zod_1.z.number().int().nonnegative().optional(),
    listCentsAtChange: zod_1.z.number().int().nonnegative().optional(),
    packageIds: zod_1.z.array(zod_1.z.string()).optional(),
    /** Location terms at the time of the change (2026-08-26 Plan & Packages spec) — audited like any other price move. */
    freeLocationQuota: zod_1.z.number().int().nonnegative().optional(),
    additionalLocationPriceCents: zod_1.z.number().int().nonnegative().optional(),
});
exports.DealSchema = zod_1.z.object({
    id: zod_1.z.string().min(1).optional(),
    code: zod_1.z.string().min(1).optional(),
    name: zod_1.z.string().min(1).optional(),
    /** Links the deal to a product so its packages can be listed (§12/A3 Step 1: "select the product"). */
    productId: zod_1.z.string().min(1).optional(),
    /** Software package ids included (from the packages table). Unpriced here — see `packageDiscounts` and `dealPackagesTotals` for the arithmetic. */
    packageIds: zod_1.z.array(zod_1.z.string()).optional(),
    /**
     * The CommissionPlan that prices what a partner earns on this deal
     * (2026-08-29). Third tier of billing-service's `pickPlan` cascade —
     * offer override -> partner pin -> THIS -> global default.
     *
     * Supersedes the flat `commissionMode`/`percentageRate`/`flatFee` trio this
     * schema deliberately does not model (they survive via `.passthrough()`):
     * those express one rate for the whole deal, which cannot say "20% on
     * software, $50 a terminal". They are still read as the fallback for every
     * deal built before plans existed, so nothing has to be migrated.
     */
    commissionPlanId: zod_1.z.string().min(1).optional(),
    /**
     * packageId -> the discount applied to that package's list price on the
     * way to `priceCents` (§12/A3). SAME shape as `PlanAssignment.discounts.
     * perPackage` — see `DiscountSchema` in `planAssignment.ts` for why
     * exactly one of `percentOff`/`amountOffCents` is required. Attributable
     * BY DESIGN: a discount that cannot be traced to the package it was
     * given on is the "unattributable lump sum" the user explicitly asked to
     * get away from.
     */
    packageDiscounts: zod_1.z.record(zod_1.z.string(), planAssignment_1.DiscountSchema).optional(),
    /**
     * Device (hardware, `device_finance`) SKU ids included with this deal —
     * the "whether device packages are included" clause of §12/A2. This is
     * ADDITIVE per this task's brief; `deals.ts` already carries the exact
     * same concept under the pre-existing name `deviceSkuIds` (from the
     * predecessor onboarding-billing-devices plan). The handler keeps the
     * two in lockstep — `devicePackageIds` is never an independently-written
     * second list — so the names can never disagree about which devices are
     * on the deal. See `deals.ts` for the single-source-of-truth mechanics.
     */
    devicePackageIds: zod_1.z.array(zod_1.z.string()).optional(),
    /**
     * `skuId -> what this deal changes about that device finance option` — the
     * hardware counterpart to `packageDiscounts`, and keyed the same way: by the
     * thing the money is attributable to.
     *
     * Only SKUs in `devicePackageIds`/`deviceSkuIds` are meaningful here; a term
     * for a device the deal does not include describes money that never moves,
     * and writers trim it exactly as `packageDiscounts` is trimmed to
     * `packageIds`. See `dealDevice.ts` for the arithmetic and for why the terms
     * are keyed by finance option rather than by device.
     *
     * ADDITIVE and optional: a deal that predates this carries none, which reads
     * as "every device on this deal bills at catalog list" — what such a deal has
     * always done.
     */
    deviceTerms: zod_1.z.record(zod_1.z.string(), dealDevice_1.DealDeviceTermSchema).optional(),
    /**
     * `packageId -> an INTRODUCTORY price for that package` — "$5 for 12 months,
     * then $99".
     *
     * The software counterpart of a device's `intro`, and deliberately the SAME
     * `PriceIntroSchema`, so one phase model (`pricePhases.ts`) covers software
     * and hardware rather than two that drift.
     *
     * ---------------------------------------------------------------------------
     * HOW IT RELATES TO `packageDiscounts`
     * ---------------------------------------------------------------------------
     *
     * They are different offers and both may be set:
     *
     *  - `packageDiscounts` changes the price PERMANENTLY. It is what
     *    `dealPackagesTotals` and `priceCents` are computed from, and it is the
     *    price the subscription settles at.
     *  - `packageIntros` changes the price for a FIXED NUMBER OF MONTHS, after
     *    which the package reverts to its discounted (or list) price.
     *
     * So a package can be sold at "$5 for a year, then $79" by carrying an intro
     * of 500/12 and a discount taking $99 to $79. `introPriceCents` is an
     * absolute price, not a further reduction — an operator setting an intro is
     * naming what the customer pays, and expressing it as a discount-on-a-discount
     * is how two people arrive at two different numbers.
     *
     * ⚠️ `priceCents` remains the SETTLED price and does NOT reflect the intro.
     * A deal's stored price is what the tenant pays once every introductory
     * period is over; the intro is a schedule on top of it. Anything quoting a
     * first-month figure must build phases rather than read `priceCents`.
     *
     * ADDITIVE and optional — a deal that predates this carries none, which is a
     * deal with no introductory period at all.
     */
    packageIntros: zod_1.z.record(zod_1.z.string(), pricePhases_1.PriceIntroSchema).optional(),
    /**
     * THE UNIFIED OFFER MAP — `packageId | skuId -> what this deal changes about
     * that line`. One shape for software and hardware alike (`dealOffer.ts`).
     *
     * Supersedes `packageDiscounts`, `packageIntros` and `deviceTerms`, none of
     * which are removed: a deal written before this carries the old fields and
     * still prices identically, because `resolveOffer` reads either. New writes
     * go here, and where both exist THIS WINS OUTRIGHT rather than merging — a
     * merge would have an offer the operator removed reappear from the old field.
     *
     * Keyed by packageId for software and skuId for a device finance option,
     * which are already distinct id spaces, so one map holds both without a
     * prefix or a discriminator.
     */
    offerTerms: zod_1.z.record(zod_1.z.string(), dealOffer_1.DealOfferTermSchema).optional(),
    /**
     * THE deal's own price, in integer cents — what a tenant who redeems
     * this code actually pays. AUTHORITATIVE at signup regardless of what
     * `packageIds`, net of `packageDiscounts`, sum to (§12/A2 — "when user
     * register and enter deal code regardless of what packages in there this
     * price will be respected"). Deliberately carries no built-in
     * reconciliation against that sum: see `dealPackagesTotals` for the
     * number to compare it against, and surface the difference rather than
     * silently overriding either one.
     */
    priceCents: zod_1.z.number().int().min(0).optional(),
    trialDays: zod_1.z.number().int().min(0).max(planAssignment_1.MAX_TRIAL_DAYS).optional(),
    /**
     * LOCATION TERMS (2026-08-26 Plan & Packages spec) — every deal defines
     * its own location economics, so a tenant's quota/extra-location price
     * exist from the moment they sign up on the deal:
     *
     * - `freeLocationQuota`: how many locations the deal includes free.
     *   Seeds `tenant.freeLocationQuota` at signup / deal application.
     * - `additionalLocationPriceCents`: monthly price for each location
     *   beyond the quota, in integer cents. The catalog `addl_location`
     *   SKU is the fallback for direct (no-deal) signups. Per-location
     *   admin comp/discount applies ON TOP of this price at approval.
     *
     * Schema-OPTIONAL (shared-types rule: an added field is never required —
     * legacy deals must still parse); deals.ts REQUIRES both on create and
     * on any update, so every deal touched after this ships carries them.
     */
    freeLocationQuota: zod_1.z.number().int().min(0).optional(),
    additionalLocationPriceCents: zod_1.z.number().int().min(0).optional(),
    /**
     * @deprecated superseded by `priceCents` (§12/A2, spec D11). Never
     * removed — existing deals keep reading from it, and a deal minted
     * before this schema existed may carry only this field.
     */
    stripePriceId: zod_1.z.string().optional(),
    /**
     * @deprecated superseded by `priceCents` (§12/A2, spec D11). Never
     * removed — existing deals keep reading from it.
     */
    monthlyAmount: zod_1.z.number().optional(),
    /**
     * THE PAPERWORK THIS DEAL SELLS UNDER (2026-08-30) — hardware and software
     * agreements and policies, the return/refund policy, and any line the
     * operator named themselves. See `dealDocuments.ts` for the shape and for
     * why this lives on the deal rather than on each device SKU.
     *
     * ADDITIVE and optional. A deal that predates it carries none, which
     * `dealDocumentsConfigured` reads as "not answered here" — checkout then
     * falls back to `BillingSku.agreementDocumentIds` exactly as before.
     */
    documents: dealDocuments_1.DealDocumentsSchema.optional(),
    /**
     * THE PARTNER'S OWN PAPERWORK for this deal (2026-08-30) — the commission
     * agreement a partner signs before they may sell it, and the policies they
     * are held to while doing so.
     *
     * DELIBERATELY NOT A THIRD SECTION INSIDE `documents`. That object is the
     * CUSTOMER's binder: `dealDocumentsConfigured` switches checkout away from
     * the SKU fallback based on whether ANY of it is set, and
     * `resolveDealDocuments` walks its sections to build the list a merchant is
     * asked to sign. A commission agreement put in there would be handed to the
     * buyer at checkout — the partner's contract, shown to the merchant, and
     * counted as one of the merchant's required signatures. Different signer,
     * different moment, different file.
     *
     * Reuses `DealDocumentSectionSchema` because the SHAPE is identical (a list
     * to sign, a list to accept); only the audience differs.
     *
     * ADDITIVE and optional — a deal that predates it carries none, which reads
     * as "this deal asks a partner for nothing", exactly what such a deal does
     * today.
     */
    commissionDocuments: dealDocuments_1.DealDocumentSectionSchema.optional(),
    /** Who created this deal (2026-08-24 spec §3.4). ADMIN-ONLY — must never appear on a public deal endpoint's response (getLandingPageDeals, getPublicDeal, getDealByCode). */
    createdBy: zod_1.z.string().optional(),
    /** Who most recently changed this deal's pricing (2026-08-24 spec §3.4). Same admin-only visibility as `createdBy`. */
    updatedBy: zod_1.z.string().optional(),
    /** Append-only audit trail of pricing/package changes, capped at 20 most recent entries — see `appendPricingHistory`. Admin-only, never public. */
    pricingHistory: zod_1.z.array(exports.DealPricingHistoryEntrySchema).optional(),
    /**
     * FREE-FORM TAG IDS (2026-09-03) — how the deals list is grouped and
     * filtered. Ids reference the `DEAL_TAGS` registry (see `DealTagSchema`),
     * never labels: renaming or recolouring a tag must not have to rewrite
     * every deal carrying it.
     *
     * A tag is presentation only. Nothing prices, gates or bills off it —
     * which is exactly why it is safe to let an operator invent one mid-flow.
     * ADDITIVE and optional: a deal with none is "Untagged", which is how
     * every deal predating this reads.
     */
    tags: zod_1.z.array(zod_1.z.string()).optional(),
    /**
     * LINEAGE (2026-09-03) — the deal this one was COPIED FROM, and the top of
     * the family it belongs to. `rootDealId` is denormalised rather than walked:
     * a family may run to a thousand variants, and grouping them must not cost a
     * read per generation.
     *
     * ⚠️ DISPLAY ONLY. A copy is a wholly independent deal — its own code,
     * price, packages, documents, commission plan and versions. Nothing is
     * inherited at read time and no write ever cascades: editing a parent must
     * never move the price of a variant a merchant already signed. These two
     * fields exist so the list can DRAW where a deal came from, and for nothing
     * else. Never introduce a resolver that reads through them.
     *
     * A root deal carries neither field. `parentDealId` may point at another
     * variant (families nest arbitrarily deep); `rootDealId` always points at
     * the top, so a family is one query rather than a recursive walk.
     */
    parentDealId: zod_1.z.string().min(1).optional(),
    rootDealId: zod_1.z.string().min(1).optional(),
    /**
     * REDEEMABLE BY CODE (2026-09-03) — this offer may be applied by a customer
     * who TYPES its code, even though it is not listed anywhere public.
     *
     * The case is an ISO or rep handing a merchant an offer that must never
     * appear on the pricing page. Before this flag, `switchDeal` refused every
     * deal with `showOnLandingPage !== true`, so such an offer could be created
     * but never redeemed.
     *
     * ⚠️ OPT-IN, PER DEAL, AND DELIBERATELY NOT THE DEFAULT. The refusal it
     * relaxes is a security rule, not an oversight: a customer-reachable
     * endpoint that accepted ANY private code would let one leaked or guessed
     * code apply a partner's discount to anybody. Turning this on is an operator
     * saying "this specific code may be redeemed by whoever holds it" — so treat
     * a flagged code as a bearer token, and keep the refusal wording for a
     * non-flagged private deal IDENTICAL to an unknown code, so the endpoint
     * still cannot be used to discover which private offers exist.
     *
     * Being redeemable says nothing about being LISTED: this flag must never
     * cause a deal to appear in `getLandingPageDeals`, which filters on
     * `showOnLandingPage` alone.
     */
    redeemableByCode: zod_1.z.boolean().optional(),
}).passthrough();
/**
 * ONE TAG in the `DEAL_TAGS` registry (a single platform-settings row —
 * `{ id: 'DEAL_TAGS', tags: [...] }`), authored inline on the deals screen.
 *
 * `color` is a hex fill; the UI derives its own readable text colour rather
 * than storing a second value that could drift out of contrast with it.
 * Deleting a tag drops the id from the deals carrying it and never deletes a
 * deal — see `deal-tags-put`.
 */
exports.DealTagSchema = zod_1.z.object({
    id: zod_1.z.string().min(1),
    label: zod_1.z.string().min(1).max(40),
    color: zod_1.z.string().regex(/^#[0-9a-fA-F]{6}$/, 'color must be #rrggbb'),
});
exports.DealTagRegistrySchema = zod_1.z.object({
    tags: zod_1.z.array(exports.DealTagSchema).max(200),
});
/**
 * Sums a Deal's software packages at LIST price and at NET (after each
 * package's own `packageDiscounts` entry), so a caller can show
 * `priceCents` (the deal's authoritative price) next to what its packages
 * actually add up to (spec §12/A2; this task's Step 4) — WITHOUT either
 * number silently overriding the other. The difference
 * (`priceCents - totals.netCents`) is exactly the number an operator needs
 * to see: the deal priced below or above the sum of its discounted parts.
 *
 * Pure and total, mirroring `composeSubscription`'s discount handling in
 * `planAssignment.ts`: each package's discount is applied and rounded ONCE
 * via `applyDiscount` (never accumulated across further arithmetic), and a
 * duplicate package id is deduped before summing so a malformed caller
 * cannot double-count one package's price. A package named in `packageIds`
 * but missing from `pricesByPackageId` contributes 0 to both totals and is
 * named in `missingPackageIds` rather than silently omitted — an unpriced
 * or unknown package is a data gap the caller must report, not a reason to
 * crash a preview.
 *
 * ⚠️ SINGLE-LOCATION, DELIBERATELY, UNLIKE `composeSubscription`. A Deal's
 * `priceCents` is the price for the tenant's FIRST location only — the
 * price an operator quotes and a customer sees at signup. It is NOT a
 * tenant-wide total across every location the tenant will ever have.
 * `composeSubscription` (`planAssignment.ts`) multiplies a `scope:
 * 'location'` package's list price across every billing location (see its
 * `targetLocationIds` handling); this function does no such thing — it has
 * no `locationIds` argument at all, because a Deal is redeemed once, at
 * signup, before any additional location exists. Additional locations
 * price through the existing `location-billing-service` machinery (free
 * quota, per-location price, approval), which already works and is
 * untouched by this plan. Two functions that both sum "packages x prices"
 * look interchangeable; they answer different questions, and reading this
 * one's `netCents` as a tenant's eventual multi-location bill would
 * understate it.
 */
function dealPackagesTotals(packageIds, packageDiscounts, pricesByPackageId) {
    const totals = { listCents: 0, discountCents: 0, netCents: 0, missingPackageIds: [] };
    const uniquePackageIds = Array.from(new Set(packageIds ?? []));
    for (const packageId of uniquePackageIds) {
        const price = pricesByPackageId[packageId];
        if (!price) {
            totals.missingPackageIds.push(packageId);
            continue;
        }
        const listCents = Math.max(0, price.basePriceCents);
        const netCents = (0, planAssignment_1.applyDiscount)(listCents, packageDiscounts?.[packageId]);
        totals.listCents += listCents;
        totals.netCents += netCents;
    }
    totals.discountCents = totals.listCents - totals.netCents;
    return totals;
}
const PRICING_HISTORY_CAP = 20;
/**
 * Appends one pricing-history entry and caps the result at the most recent
 * `PRICING_HISTORY_CAP` (20), dropping the oldest — an append-only audit
 * trail (spec §3.4) that cannot grow unbounded on a long-lived deal.
 */
function appendPricingHistory(existing, entry) {
    return [...(existing ?? []), entry].slice(-PRICING_HISTORY_CAP);
}
/**
 * True when a change is pricing-relevant enough to warrant a new
 * `appendPricingHistory` entry: `priceCents` differs, or the `packageIds`
 * SETS differ (order-insensitive; `undefined` is treated the same as `[]`
 * so an unset list and an empty list are not a "change").
 */
function pricingChanged(prev, next) {
    if (prev.priceCents !== next.priceCents)
        return true;
    const a = new Set(prev.packageIds ?? []);
    const b = new Set(next.packageIds ?? []);
    if (a.size !== b.size)
        return true;
    for (const id of a)
        if (!b.has(id))
            return true;
    return false;
}
/**
 * Strikethrough gating (spec §3.1): show a "was" price ONLY when both
 * numbers are known and listCents > priceCents > 0. Anything else renders
 * the deal price alone — never $0.00, never an invented number.
 */
function dealStrikethrough(listCents, priceCents) {
    const list = typeof listCents === 'number' && Number.isFinite(listCents) ? listCents : 0;
    const price = typeof priceCents === 'number' && Number.isFinite(priceCents) ? priceCents : 0;
    if (!(list > price && price > 0))
        return { show: false, wasCents: 0, percentOff: 0 };
    return { show: true, wasCents: list, percentOff: Math.round((1 - price / list) * 100) };
}
//# sourceMappingURL=deal.js.map